<template>
  <!--
    Приёмка 29.09 (решение USER «свой диалог договора»): в диалоге приёма
    платежа договорного бронирования — что к оплате по графику (аванс,
    комиссия за оформление, строки по сегодня, ближайший платёж) и быстрые
    суммы для поля платежа. Платёж раскладывает api (аллокатор), описание
    платежа api заполняет само — что именно погашено. Базовые компоненты
    Vuetify, цвета из темы.
  -->
  <v-card outlined class="mb-3" data-testid="rent-to-own-payment-summary">
    <v-card-subtitle class="pb-0 font-weight-medium">
      {{ $t("rent_to_own.payment.title", { number: contractNumber }) }}
    </v-card-subtitle>
    <v-card-text class="pt-2">
      <v-progress-linear v-if="loading" indeterminate data-testid="rent-to-own-payment-loading"></v-progress-linear>
      <div v-else-if="failed" class="text-body-2 error--text" data-testid="rent-to-own-payment-error">
        {{ $t("rent_to_own.payment.load_error") }}
      </div>
      <!-- Договор закрыли, пока карточка была открыта: платёж примется как обычный. -->
      <div v-else-if="closed" class="text-body-2" data-testid="rent-to-own-payment-closed">
        {{ $t("rent_to_own.payment.closed") }}
      </div>
      <template v-else>
        <v-simple-table v-if="lines.length" dense>
          <tbody>
            <tr v-for="line in lines" :key="line.key" :data-testid="`rent-to-own-payment-line-${line.key}`">
              <td class="pl-0">{{ line.label }}</td>
              <td class="pr-0 text-right text-no-wrap">{{ fmtMoney(String(line.amount)) }}</td>
            </tr>
          </tbody>
        </v-simple-table>
        <div v-if="!breakdown.dueNow" class="text-body-2 mt-2" data-testid="rent-to-own-payment-nothing-due">
          {{ $t("rent_to_own.payment.nothing_due") }}
        </div>
        <div v-if="breakdown.options.length" class="mt-3">
          <v-btn v-for="option in breakdown.options" :key="option.key" small outlined block color="primary"
            class="mb-2 text-none"
            :data-testid="`rent-to-own-payment-fill-${option.key}`" @click="$emit('fill', option.amount)">
            {{ $t(`rent_to_own.payment.fill_${option.key}`, { date: option.due_on ? fmtDate(option.due_on) : "" }) }} — {{ fmtMoney(String(option.amount)) }}
          </v-btn>
        </div>
        <div class="text-caption text--secondary">{{ $t("rent_to_own.payment.hint") }}</div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from "moment";
import { rentToOwnApi } from "@/api/rentToOwn";
import rentToOwnFormatMixin from "@/mixins/rentToOwnFormatMixin";
import { paymentBreakdown } from "@/components/dashboard/rent_to_own/rentToOwnPayment";

export default {
  name: "RentToOwnPaymentSummary",
  mixins: [rentToOwnFormatMixin],
  props: {
    contract: { type: Object, required: true },
  },
  data() {
    return { loading: true, failed: false, closed: false, installments: [], today: null };
  },
  computed: {
    contractNumber() {
      return this.contract.number || `#${this.contract.id}`;
    },
    breakdown() {
      // «Сегодня» — дата компании из api (строки графика — в её дате); без неё — браузера.
      return paymentBreakdown(this.installments, this.today || moment().format("YYYY-MM-DD"));
    },
    lines() {
      const b = this.breakdown;
      const lines = [];
      if (b.advance) lines.push({ key: "advance", label: this.$t("rent_to_own.payment.advance"), amount: b.advance });
      if (b.setupFee) lines.push({ key: "setup_fee", label: this.$t("rent_to_own.payment.setup_fee"), amount: b.setupFee });
      if (b.scheduleDue) lines.push({ key: "schedule_due", label: this.scheduleDueLabel(b.scheduleDuePeriod), amount: b.scheduleDue });
      if (b.next) {
        lines.push({ key: "next", label: this.$t("rent_to_own.payment.next", { date: this.fmtDate(b.next.due_on) }), amount: b.next.amount });
      }
      return lines;
    },
  },
  created() {
    this.load();
  },
  methods: {
    scheduleDueLabel(period) {
      if (period.from === period.to) return this.$t("rent_to_own.payment.schedule_due_one", { date: this.fmtDate(period.from) });
      return this.$t("rent_to_own.payment.schedule_due", { from: this.fmtDate(period.from), to: this.fmtDate(period.to) });
    },
    // Сводка — подсказка к полю: без графика сумму вводят вручную, платёж
    // сохраняется как обычно, поэтому ошибка только подписью.
    load() {
      this.loading = true;
      this.failed = false;
      return rentToOwnApi
        .getContract(this.axios, this.contract.id)
        .then((data) => {
          const contract = (data && data.contract) || {};
          this.installments = contract.installments || [];
          this.today = contract.today || null;
          this.closed = contract.lifecycle !== "active";
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.failed = true;
        });
    },
  },
};
</script>
